import { Grid } from '@mui/material'
import { Formik } from 'formik'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getLocalizedRoleName } from '../../../../common/roleHelper'
import { getErrMsg, toastNeg, toastPos } from '../../../../common/toastHelper'
import {
  IEditUserProject,
  ILinkedSkill,
  INewEmployer,
  INewProject,
  IOtherEmployerDetailsForm,
  IPrivateProjectFormPackage,
  IUserProject,
} from '../../../../interfaces/projects/IProjects'
import { ISkillProject } from '../../../../interfaces/skills/ISkills'
import { useStore } from '../../../../Provider'
import DeleteConfirmation from '../../../OPcomponents/DeleteConfirmation'
import { ExitConfirmation } from '../../../OPcomponents/ExitConfirmation'
import AutoSave from '../../../OPcomponents/FormikAutosave'
import { OPDivider } from '../../../OPcomponents/OPDivider'
import { privateWorkHistorySchema } from '../../../OPcomponents/ValidationSchemas'
import { OPSkillsButtons } from '../../OPSkillsButtons'
import AddNewEmployer from './AddNewEmployer'
import NewPersonalPrivateProject from './NewPersonalPrivateProject'
import NewPrivateProject from './NewPrivateProject'

interface IAddNewPrivateProject {
  isProjectSite?: boolean
  isCreateNewProject?: boolean
  isEdit?: boolean
  collapseHandler?: () => void
  data?: IPrivateProjectFormPackage
}

export interface initValues {
  employer: IOtherEmployerDetailsForm
  project: INewProject
  userProject: IUserProject
}

const AddNewPrivateProject = (props: IAddNewPrivateProject): JSX.Element => {
  const { isProjectSite = false, isCreateNewProject = false, isEdit = false, collapseHandler, data } = props
  const { t } = useTranslation()
  const rootStore = useStore()
  const { validatedUser } = rootStore.loginStore
  const {
    currentEmployerDetails,
    newEmployerName,
    getAllEmployerDetails,
    allOtherEmployerDetails,
    postEmployer,
    postProject,
    postUserProject,
    editUserOwnedProject,
    editEmployer,
    deleteUserProject,
    getAllProjects,
    showAddPrivateProjectModalHandler,
    showModalHandler,
  } = rootStore.projectStore

  const { postNewSkillProjectLinks, deleteNewSkillProjectLinks } = rootStore.skillStore

  const { getUserPrivateWorkHistory } = rootStore.personStore

  const [projectSkills, setProjectSkills] = useState<ISkillProject[]>([])
  const [deleteProjectConfirmation, setDeleteProjectConfirmation] = useState(false)

  // Keep last-saved baseline for diffing updates across multiple consecutive edits
  const baselineLinkedSkillsRef = useRef<ISkillProject[]>([])

  // Initialize both projectSkills and baseline from incoming data
  useEffect(() => {
    const links: ISkillProject[] = []
    if (data?.userDetails.linkedSkills) {
      data.userDetails.linkedSkills.forEach((skill: ILinkedSkill) => {
        links.push({
          userId: validatedUser,
          projectId: data?.project.projectId,
          skillId: skill.skillId,
          textFi: skill.skillNameFi,
          textEng: skill.skillNameEng,
        })
      })
    }
    setProjectSkills(links)
    baselineLinkedSkillsRef.current = links
  }, [data?.project.projectId, data?.userDetails.linkedSkills, validatedUser])

  const linksModified = _.isEqual(_.sortBy(projectSkills, ['skillId']), _.sortBy(baselineLinkedSkillsRef.current, ['skillId']))

  const initValues: initValues = {
    employer: {
      name: data?.employerDetails.name || newEmployerName || '',
      userId: validatedUser,
      jobTitle: data?.employerDetails.jobTitle || '',
      jobDescriptionFi: data?.employerDetails.jobDescriptionFi || '',
      jobDescriptionEng: data?.employerDetails.jobDescriptionEng || '',
      startDate: data?.employerDetails.startDate || undefined,
      endDate: data?.employerDetails.endDate || undefined,
      employerId: data?.employerDetails.employerId || -1,
    },
    project: {
      userId: validatedUser,
      name: data?.project.name || '',
      branchId: data?.project.branchId || -1,
      description: data?.project.description || '',
      descriptionEng: data?.project.descriptionEng || '',
      customer: data?.project.customer || '',
      public: data?.project.public || false,
      isNetum: false,
      canUseAsReference: data?.project.canUseAsReference || false,
      employerId: data?.project.employerId || currentEmployerDetails.employerId || -1,
    },
    userProject: {
      userId: validatedUser,
      projectId: data?.userDetails.projectId || -1,
      startDate: data?.userDetails.startDate || undefined,
      endDate: data?.userDetails.endDate || undefined,
      manDays: (() => {
        const rawManDays = data?.userDetails.manDays || 0
        const rawManMonths = data?.userDetails.manMonths || 0
        const rawUsingManDays = data?.userDetails.usingManDays ?? true

        if (rawUsingManDays && rawManDays === 0 && rawManMonths > 0) {
          return Math.round(rawManMonths * 21)
        }
        return rawManDays
      })(),
      manMonths: (() => {
        const rawManDays = data?.userDetails.manDays || 0
        const rawManMonths = data?.userDetails.manMonths || 0
        const rawUsingManDays = data?.userDetails.usingManDays ?? true

        if (!rawUsingManDays && rawManMonths === 0 && rawManDays > 0) {
          return Math.round(rawManDays / 21)
        }
        return rawManMonths
      })(),
      usingManDays: data?.userDetails.usingManDays ?? true,
      usingManMonths: data?.userDetails.usingManMonths ?? false,
      descriptionFi: data?.userDetails.descriptionFi || '',
      descriptionEng: data?.userDetails.descriptionEng || '',
      roleIds:
        data?.userDetails.roles.map((role) => ({ value: role.roleId.toString(), key: role.roleId, text: getLocalizedRoleName(role) })) ||
        [],
      isKeyProject: data?.userDetails.isKeyProject ?? true,
    },
  }

  useEffect(() => {
    if (allOtherEmployerDetails.length === 0) {
      getAllEmployerDetails(validatedUser)
    }
  }, [allOtherEmployerDetails.length, getAllEmployerDetails, validatedUser])

  const handleSubmitNew = async (values: initValues) => {
    try {
      if (values.employer.employerId === -1 && values.project.employerId === -1) {
        const newEmp = await postEmployer(values.employer as INewEmployer)
        values.project.employerId = newEmp
      }
      const projId = await postProject(values.project)

      const personProject: IEditUserProject = {
        userId: validatedUser,
        projectId: projId,
        startDate: values.userProject.startDate,
        endDate: values.userProject.endDate,
        workAmount: values.userProject.usingManDays ? values.userProject.manDays : values.userProject.manMonths,
        usingManDays: values.userProject.usingManDays,
        usingManMonths: !values.userProject.usingManDays,
        descriptionFi: values.userProject.descriptionFi,
        descriptionEng: values.userProject.descriptionEng,
        roleIds: values.userProject.roleIds.map((role) => role.key) || [],
        isKeyProject: values.userProject.isKeyProject,
      }
      await postUserProject(personProject)
      if (projectSkills.length > 0) {
        const toPost = projectSkills.map((s) => ({ ...s, projectId: projId }))
        await postNewSkillProjectLinks(toPost)
      }
      toastPos(t('Toasts.ProjectCreate'))
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.ProjectCreateError'))
    } finally {
      handleClose()
    }
  }

  const handleSubmitEdit = async (values: initValues) => {
    try {
      if (values.employer.employerId === -1) {
        const newEmp = await postEmployer(values.employer as INewEmployer)
        values.employer.employerId = newEmp
      } else {
        const editedEmployer: IOtherEmployerDetailsForm = {
          employerId: values.employer.employerId,
          name: values.employer.name,
          userId: validatedUser,
          jobTitle: values.employer.jobTitle,
          jobDescriptionFi: values.employer.jobDescriptionFi,
          jobDescriptionEng: values.employer.jobDescriptionEng,
          startDate: values.employer.startDate,
          endDate: values.employer.endDate,
        }
        await editEmployer(editedEmployer)
      }

      if (values.employer.employerId !== values.project.employerId) {
        values.project.employerId = values.employer.employerId
      }

      const editedProject = {
        userId: validatedUser,
        projectId: values.userProject.projectId,
        name: values.project.name,
        projectDescription: values.project.description,
        projectDescriptionEng: values.project.descriptionEng,
        customer: values.project.customer,
        branchId: values.project.branchId,
        employerId: values.project.employerId,
        canUseAsReference: values.project.canUseAsReference,
        startDate: values.userProject.startDate,
        endDate: values.userProject.endDate,
        public: values.project.public,
        workAmount: values.userProject.usingManDays ? values.userProject.manDays : values.userProject.manMonths,
        usingManDays: values.userProject.usingManDays,
        usingManMonths: !values.userProject.usingManDays,
        userDescriptionFi: values.userProject.descriptionFi,
        userDescriptionEng: values.userProject.descriptionEng,
        roleIds: values.userProject.roleIds.map((role) => role.key) || [],
        contactPerson: values.project.contactPerson,
        isKeyProject: values.userProject.isKeyProject,
      }

      // Diff using the last-saved baseline
      const baseline = baselineLinkedSkillsRef.current
      const removedLinks = _.differenceBy(baseline, projectSkills, 'skillId')
      const addedLinks = _.differenceBy(projectSkills, baseline, 'skillId')

      await editUserOwnedProject(editedProject)
      if (removedLinks.length > 0) await deleteNewSkillProjectLinks(removedLinks)
      if (addedLinks.length > 0) await postNewSkillProjectLinks(addedLinks)
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.ProjectEditError'))
    }
  }

  const handleClose = async () => {
    showAddPrivateProjectModalHandler(false)
    setTimeout(async () => {
      await getAllProjects(validatedUser, false)
      await getUserPrivateWorkHistory(validatedUser, false)
      await getAllEmployerDetails(validatedUser, false)
    }, 500)
  }

  const deleteProject = async (id: number) => {
    try {
      await deleteUserProject(validatedUser, id, true)
      toastPos(t('Toasts.ProjectUserDelete'), true)
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.ProjectUserDeleteError'), true)
    } finally {
      handleClose()
    }
  }

  useEffect(() => {
    return () => {
      handleClose()
    }
  }, [])

  const submitHandler = (values: initValues) => (isEdit ? handleSubmitEdit(values) : handleSubmitNew(values))

  return (
    <Grid container rowSpacing={4} className={isProjectSite || isCreateNewProject ? '' : 'gridcontainer--formcontainer__itempaddings'}>
      <Formik initialValues={initValues} validationSchema={privateWorkHistorySchema} onSubmit={submitHandler}>
        {({ isValid, dirty, handleSubmit, isSubmitting, values }) => (
          <>
            <ExitConfirmation when={dirty} />
            <Grid item xs={12}>
              <AddNewEmployer deleteProjectConfirmation={deleteProjectConfirmation} isEdit={isEdit} />
            </Grid>
            <Grid item xs={12}>
              <OPDivider />
            </Grid>
            <Grid item xs={12}>
              <NewPrivateProject deleteProjectConfirmation={deleteProjectConfirmation} />
            </Grid>
            <Grid item xs={12}>
              <NewPersonalPrivateProject
                isNewProject={!isEdit}
                deleteProjectConfirmation={deleteProjectConfirmation}
                setProjectSkills={setProjectSkills}
                projectSkills={projectSkills}
              />
            </Grid>
            <Grid xs={12} item>
              <OPSkillsButtons
                isDelete={isEdit}
                onDelete={() => setDeleteProjectConfirmation(true)}
                bothDisabled={isSubmitting || deleteProjectConfirmation}
                disabled={!isValid || (!dirty && linksModified) || isSubmitting}
                noPadding
                bottomPadding
                onSubmit={handleSubmit}
                onClose={() => {
                  showModalHandler(false)
                  showAddPrivateProjectModalHandler(false)
                }}
                onAutosave={isEdit}
              />
            </Grid>
            {isEdit && (
              <DeleteConfirmation
                open={deleteProjectConfirmation}
                whatToDelete='Project'
                deleteHandler={() => deleteProject(values.userProject.projectId)}
                handleClose={() => setDeleteProjectConfirmation(false)}
              />
            )}
            {isEdit && <AutoSave />}
          </>
        )}
      </Formik>
    </Grid>
  )
}
export default observer(AddNewPrivateProject)
